// PaymentReport.js

import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Spinner,
  useTheme,
  useColorMode,
} from "@chakra-ui/react";
import moment from "moment";
import { cqAPI } from "../../lib/Auth";
import "react-datepicker/dist/react-datepicker.css";
import "./PaymentReport.css";
import { useReactToPrint } from "react-to-print";

function DiscountReport() {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const theme = useTheme();
  const { colorMode } = useColorMode();

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const dateFields = [
    "startDate",
    "firstAppointmentDate",
    "lastAppointmentDate",
  ];
  const numericFields = ["monthlyFee"];

  const sortedTransactions = useMemo(() => {
    let sortableItems = [...payments];
    if (sortConfig !== null && sortConfig.key) {
      sortableItems.sort((a, b) => {
        let va = a[sortConfig.key];
        let vb = b[sortConfig.key];

        if (dateFields.includes(sortConfig.key)) {
          va = new Date(va);
          vb = new Date(vb);
        } else if (numericFields.includes(sortConfig.key)) {
          va = parseFloat(va.replace(/[^0-9.-]+/g, ""));
          vb = parseFloat(vb.replace(/[^0-9.-]+/g, ""));
        } else {
          va = va.toString().toLowerCase();
          vb = vb.toString().toLowerCase();
        }

        if (va < vb) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (va > vb) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [payments, sortConfig]);
  const showLocationColumn = sortedTransactions.some((payment) => payment.location_name);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const fetchPayments = async () => {
    setIsLoading(true);

    try {
      const response = await cqAPI.get(`/reports/discount`);

      if (response.type === "ok") {
        const transformData = (data) => {
          const fieldNames = [
            "plan",
            "startDate",
            "status",
            "monthlyFee",
            "patient",
            "visitedOffices",
            "firstAppointmentDate",
            "lastAppointmentDate",
            "location_name"
          ];
          return data.map((entry) => {
            let obj = {};
            fieldNames.forEach((field, index) => {
              obj[field] = entry[index];
            });
            return obj;
          });
        };

        setPayments(transformData(response.data));
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  if (isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100%"
        width="100%"
        position="relative"
      >
        <Spinner className="centered-spinner" color="blue.500" size="xl" />
      </Flex>
    );
  }

  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  const totalSum = payments.reduce((acc, cur) => {
    const total = parseFloat(cur.monthlyFee.replace(/[^0-9.-]+/g, ""));
    return acc + total;
  }, 0);

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex data-report-download-target justify="flex-end" mb={2} className="no-print" />
      <div ref={componentRef}>
        <VStack pacing={0} align="stretch">
          <Flex borderBottom="1px" borderColor="gray.200" p={2}>
            <HStack spacing={2} flex="1">
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("plan")}
                cursor="pointer"
              >
                Plan
              </Text>
              {showLocationColumn && (
                <Text fontSize="sm" flexBasis="10%" fontWeight="bold">
                  Location
                </Text>
              )}
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("startDate")}
                cursor="pointer"
              >
                Start Date
              </Text>
              <Text
                fontSize="sm"
                flexBasis="8%"
                fontWeight="bold"
                onClick={() => requestSort("status")}
                cursor="pointer"
              >
                Status
              </Text>
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("monthlyFee")}
                cursor="pointer"
              >
                Monthly Fee
              </Text>
              <Text
                fontSize="sm"
                flexBasis="15%"
                fontWeight="bold"
                onClick={() => requestSort("patient")}
                cursor="pointer"
              >
                Patient
              </Text>
              <Text
                fontSize="sm"
                flexBasis="10%"
                fontWeight="bold"
                onClick={() => requestSort("visitedOffices")}
                cursor="pointer"
              >
                Visited Offices
              </Text>
              <Text
                fontSize="sm"
                flexBasis="15%"
                fontWeight="bold"
                onClick={() => requestSort("firstAppointmentDate")}
                cursor="pointer"
              >
                First Appointment Date
              </Text>
              <Text
                fontSize="sm"
                flexBasis="15%"
                fontWeight="bold"
                onClick={() => requestSort("lastAppointmentDate")}
                cursor="pointer"
              >
                Last Appointment Date
              </Text>
            </HStack>
          </Flex>
          {sortedTransactions.map((payment, index) => (
            <Flex key={index} p={2}>
              <HStack spacing={2} flex="1">
                <Text fontSize="sm" flexBasis="10%">
                  {payment.plan}
                </Text>
                {showLocationColumn && (
                  <Text fontSize="sm" flexBasis="10%">
                    {payment.location_name || "-"}
                  </Text>
                )}
                <Text fontSize="sm" flexBasis="10%">
                  {moment(payment.startDate, "MM/DD/YYYY").format("MMM D, YYYY")}
                </Text>
                <Text fontSize="sm" flexBasis="8%">
                  {payment.status}
                </Text>
                <Text fontSize="sm" flexBasis="10%">
                  {payment.monthlyFee}
                </Text>
                <Text fontSize="sm" flexBasis="15%">
                  {payment.patient}
                </Text>
                <Text fontSize="sm" flexBasis="10%">
                  {payment.visitedOffices}
                </Text>
                <Text fontSize="sm" flexBasis="15%">
                  {moment(payment.firstAppointmentDate, "MM/DD/YYYY").format(
                    "MMM D, YYYY"
                  )}
                </Text>
                <Text fontSize="sm" flexBasis="15%">
                  {moment(payment.lastAppointmentDate, "MM/DD/YYYY").format(
                    "MMM D, YYYY"
                  )}
                </Text>
              </HStack>
            </Flex>
          ))}
        </VStack>
        <Box textAlign="right" mt={4}>
          <Text fontWeight="bold">Total Monthly Fees: ${totalSum.toFixed(2)}</Text>
        </Box>
      </div>
    </Box>
  );
}

export default DiscountReport;
